import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { IEditBox } from "@app/dashboard/types/innerLayout/payment_integrations/edit-integrations";
import { Card } from "../../../../../../components/inner_layout/Cards/CardWrapper";
import { Button, Input } from "@unified-checkout/design-system";
import { PERMISSIONS } from "@app/dashboard/constants/permissions";
import { isAccessible } from "@app/dashboard/utils/isAllowedPermission";
import styles from "./edit-card.module.scss";

export const Editbox = ({
    width,
    height,
    integrationName,
    redirectUrl,
    webhookUrl,
    onSave,
    isSaving
}: IEditBox) => {
    const { t } = useTranslation();

    const canUpdatePaymentIntegrations = isAccessible(PERMISSIONS.CAN_UPDATE_PAYMENT_INTEGRATIONS);

    const [formValues, setFormValues] = useState({
        integrationName,
        webhookUrl,
        redirectUrl
    });
    const [hasChanges, setHasChanges] = useState(false);
    const {
        integrationName: currentIntegrationName,
        webhookUrl: currentWebhookUrl,
        redirectUrl: currentRedirectUrl
    } = formValues;


    const handleInputChange = (field: keyof typeof formValues, value: string) => {
        const newValues = { ...formValues, [field]: value };
        const {
            integrationName: newIntegrationName,
            webhookUrl: newWebhookUrl,
            redirectUrl: newRedirectUrl
        } = newValues;
        setFormValues(newValues);

        const isChanged =
            newIntegrationName !== integrationName ||
            newWebhookUrl !== webhookUrl ||
            newRedirectUrl !== redirectUrl

        setHasChanges(isChanged);
    };

    const handleSave = () => {
        onSave?.({
            integrationName: currentIntegrationName,
            webhookUrl: currentWebhookUrl,
            redirectUrl: currentRedirectUrl,
        });
        setHasChanges(false);
    };

    const handleCancel = () => {
        setFormValues({
            integrationName,
            webhookUrl,
            redirectUrl
        });
        setHasChanges(false);
    };

    return (
        <Card width={width} height={height} title={""}>
            <div className={styles['section']}>
                <Input
                    type="text"
                    name="integrationName"
                    value={formValues.integrationName}
                    onChange={(e) => handleInputChange('integrationName', e.target.value)}
                    label={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.integrationName')}
                    placeholder={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.IntegrationNamePlaceholder')}
                    disabled={!canUpdatePaymentIntegrations}
                />
                <Input
                    type="text"
                    name="webhookUrl"
                    value={formValues.webhookUrl}
                    onChange={(e) => handleInputChange('webhookUrl', e.target.value)}
                    label={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.webhookUrl')}
                    placeholder={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.webhookUrlPlaceholder')}
                    disabled={!canUpdatePaymentIntegrations}
                />
                <Input
                    type="text"
                    name="redirectUrl"
                    value={formValues.redirectUrl}
                    onChange={(e) => handleInputChange('redirectUrl', e.target.value)}
                    label={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.redirectUrl')}
                    placeholder={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.redirectUrlPlaceholder')}
                    disabled={!canUpdatePaymentIntegrations}
                />
            </div>
            <div className={styles['section__button-group']}>
                {
                    hasChanges &&
                    <Button
                        size="xl"
                        color="secondary"
                        variant="outlined"
                        text={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.cancel')}
                        onClickHandler={() => handleCancel()}
                    />
                }
                {
                    canUpdatePaymentIntegrations &&
                    <Button
                        size="xl"
                        color="primary"
                        variant="contained"
                        text={t('innerLayout.paymentIntegrationsPage.table.headers.paymentIntegration.saveChanged')}
                        onClickHandler={() => handleSave()}
                        disabled={!hasChanges || isSaving}
                        loading={isSaving}
                    />
                }
            </div>
        </Card>
    );
};
